/* Mega menu behaviour.
   Open/close is still owned by the theme: HoverDisclosure for the desktop
   panels, Drawer for the mobile drawer. This file adds only what those two
   don't do — rail tab switching, and sliding between drawer panels. */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';

  function visibleFocusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  /* Desktop: the category rail is a tablist inside an already-open panel. */
  function MegaRail(container) {
    this.container = container;
    this.tabs = Array.prototype.slice.call(container.querySelectorAll('[data-mega-tab]'));
    this.panels = Array.prototype.slice.call(container.querySelectorAll('[data-mega-panel]'));
    if (!this.tabs.length) return;

    this.onTabEnter = this.onTabEnter.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.tabs.forEach(
      function (tab, index) {
        tab.addEventListener('mouseenter', this.onTabEnter);
        tab.addEventListener('focus', this.onTabEnter);
        tab.addEventListener('keydown', this.onKeydown);
        tab.megaIndex = index;
      }.bind(this)
    );
  }

  MegaRail.prototype.onTabEnter = function (event) {
    this.activate(event.currentTarget.megaIndex, false);
  };

  MegaRail.prototype.activate = function (index, moveFocus) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach(function (tab, i) {
      var selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    this.panels.forEach(function (panel, i) {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (moveFocus) this.tabs[index].focus();
  };

  MegaRail.prototype.onKeydown = function (event) {
    var current = event.currentTarget.megaIndex;
    var last = this.tabs.length - 1;
    var next = null;

    switch (event.key) {
      case 'ArrowDown':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowUp':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next, true);
  };

  /* HoverDisclosure writes aria-expanded onto the wrapper div, not the button
     the user actually focuses. Mirror it so the control reports its own state. */
  function syncTrigger(item) {
    var button = item.querySelector('[data-mega-trigger]');
    if (!button) return;

    var dropdown = item.querySelector('[data-mega-dropdown]');

    var apply = function () {
      var open = item.classList.contains('is-visible');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Re-measure as it opens, so the clamp is never based on stale layout.
      if (open && dropdown) clampDropdown(dropdown);
    };

    apply();
    new MutationObserver(apply).observe(item, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /* A dropdown is centred on its trigger, which pushes it off-screen for nav
     items near an edge. Measure and nudge it back. The panel has layout even
     while hidden, so this can run up front rather than on open. */
  function clampDropdown(el) {
    var margin = 16;

    el.style.setProperty('--mega-shift', '0px');
    var rect = el.getBoundingClientRect();
    var shift = 0;

    if (rect.right > window.innerWidth - margin) {
      shift = window.innerWidth - margin - rect.right;
    }
    if (rect.left + shift < margin) {
      shift = margin - rect.left;
    }

    el.style.setProperty('--mega-shift', Math.round(shift) + 'px');
  }

  function clampAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mega-dropdown]'), clampDropdown);
  }

  /* Mobile: slide between panels inside the open drawer. */
  function MegaDrawer(viewport) {
    this.viewport = viewport;
    this.drawer = viewport.closest('[data-drawer]') || viewport;
    this.root = viewport.querySelector('[data-drawer-level="0"]');
    this.current = this.root;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.reset = this.reset.bind(this);

    this.onSearchClick = this.focusSearch.bind(this);

    viewport.addEventListener('click', this.onClick);
    this.drawer.addEventListener('keydown', this.onKeydown);
    document.addEventListener('theme:drawer:close', this.reset);

    var searchButton = this.drawer.querySelector('[data-drawer-search]');
    if (searchButton) searchButton.addEventListener('click', this.onSearchClick);
  }

  MegaDrawer.prototype.onClick = function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener && this.viewport.contains(opener)) {
      event.preventDefault();
      this.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }

    if (event.target.closest('[data-drawer-back]')) {
      event.preventDefault();
      this.back();
    }
  };

  /* The header's search icon shows on every level, so from a sub-panel it
     returns to the root before focusing the field. */
  MegaDrawer.prototype.focusSearch = function () {
    if (this.current !== this.root) this.back();

    var input = this.root && this.root.querySelector('.search-form__input');
    if (!input) return;

    input.scrollIntoView({block: 'nearest'});
    input.focus();
  };

  MegaDrawer.prototype.open = function (id, opener) {
    var panel = this.viewport.querySelector('[data-drawer-panel="' + id + '"]');
    if (!panel) return;

    this.opener = opener || null;
    if (opener) opener.setAttribute('aria-expanded', 'true');

    panel.removeAttribute('hidden');
    // Force layout so the transform transition runs from its start value.
    void panel.offsetWidth;

    panel.classList.add('is-active');
    this.current = panel;

    this.focusPanel(panel);
  };

  MegaDrawer.prototype.back = function () {
    var panel = this.current;
    if (!panel || panel === this.root) return;

    panel.classList.remove('is-active');
    this.current = this.root;

    var hide = function (event) {
      if (event && event.target !== panel) return;
      panel.setAttribute('hidden', '');
      panel.removeEventListener('transitionend', hide);
    };
    panel.addEventListener('transitionend', hide);

    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus({preventScroll: true});
      this.opener = null;
    } else {
      this.focusPanel(this.root);
    }
  };

  /* preventScroll matters: the panel is still translated off-screen when this
     runs, and without it the browser scrolls to reach the heading and then
     settles back once the transform lands — which reads as a bounce. */
  MegaDrawer.prototype.focusPanel = function (panel) {
    var heading = panel.querySelector('[data-panel-heading]');
    if (heading) heading.focus({preventScroll: true});
  };

  MegaDrawer.prototype.reset = function () {
    Array.prototype.forEach.call(
      this.viewport.querySelectorAll('[data-drawer-level="1"]'),
      function (panel) {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      }
    );
    Array.prototype.forEach.call(
      this.viewport.querySelectorAll('[data-drawer-open]'),
      function (button) {
        button.setAttribute('aria-expanded', 'false');
      }
    );
    this.current = this.root;
    this.opener = null;
  };

  /* The theme's focus trap caches first/last when the drawer opens, so it goes
     stale the moment a panel changes. Keep Tab inside what's actually visible. */
  MegaDrawer.prototype.onKeydown = function (event) {
    if (event.key === 'Escape' && this.current !== this.root) {
      event.stopPropagation();
      this.back();
      return;
    }

    if (event.key !== 'Tab') return;

    var scope = [];
    var header = this.drawer.querySelector('.mega-drawer__header');
    if (header) scope = scope.concat(visibleFocusables(header));
    if (this.current) scope = scope.concat(visibleFocusables(this.current));
    if (!scope.length) return;

    var first = scope[0];
    var last = scope[scope.length - 1];

    if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last.focus();
    }
  };

  function init(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(root.querySelectorAll('[data-mega-menu]'), function (el) {
      if (el.megaRail) return;
      el.megaRail = new MegaRail(el);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-hover-disclosure-toggle]'), function (el) {
      if (el.megaSynced) return;
      el.megaSynced = true;
      syncTrigger(el);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-mega-drawer]'), function (el) {
      if (el.megaDrawer) return;
      el.megaDrawer = new MegaDrawer(el);
    });

    clampAll();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(clampAll, 150);
  });

  /* Web fonts land after first paint and change the panel's width. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(clampAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
